import { useMemo, useState } from 'react'
import { Plus, Trash2, UserCog } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { useToast } from '@/hooks/useToast'
import {
  useApprovalCatalog,
  useApprovalDelegations,
  useCreateApprovalDelegation,
  useRevokeApprovalDelegation,
} from '@/features/approvals/queries'
import { useMembers } from '@/features/team/queries'
import type { ApprovalDelegation, ApprovalResourceType } from '@/features/approvals/types'
import { formatDateTime } from '@/lib/locale'

function isActive(d: ApprovalDelegation, now = new Date()): boolean {
  if (d.revoked_at) return false
  const from = new Date(d.active_from)
  const to = new Date(d.active_to)
  return !Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())
    && now >= from && now <= to
}

export function ApprovalDelegationsPage() {
  const { toast } = useToast()
  const catalog = useApprovalCatalog()
  const resourceTypes = (catalog.data?.resource_types || []) as ApprovalResourceType[]
  const { data, isLoading } = useApprovalDelegations()
  const members = useMembers()
  const createMut = useCreateApprovalDelegation()
  const revokeMut = useRevokeApprovalDelegation()

  const [open, setOpen] = useState(false)
  const [delegateId, setDelegateId] = useState<string>('')
  const [activeFrom, setActiveFrom] = useState<string>(new Date().toISOString().slice(0, 10))
  const [activeTo, setActiveTo] = useState<string>(
    new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
  )
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [reason, setReason] = useState('')
  const [revokeTarget, setRevokeTarget] = useState<ApprovalDelegation | null>(null)

  const given = useMemo(
    () => (data?.delegations ?? []).filter(d => d.direction === 'given'),
    [data],
  )
  const received = useMemo(
    () => (data?.delegations ?? []).filter(d => d.direction === 'received'),
    [data],
  )

  async function onCreate() {
    const did = parseInt(delegateId, 10)
    if (!Number.isFinite(did) || did <= 0) {
      toast({ title: 'Pick a delegate', variant: 'destructive' }); return
    }
    if (!activeTo) {
      toast({ title: 'End date is required', variant: 'destructive' }); return
    }
    try {
      await createMut.mutateAsync({
        delegate_user_id: did,
        active_from: new Date(activeFrom).toISOString(),
        active_to: new Date(activeTo).toISOString(),
        resource_types: selectedTypes,
        reason: reason.trim() || undefined,
      })
      toast({ title: 'Delegation created' })
      setOpen(false)
      setDelegateId('')
      setSelectedTypes([])
      setReason('')
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to create delegation', variant: 'destructive' })
    }
  }

  async function onRevokeConfirm() {
    if (!revokeTarget) return
    try {
      await revokeMut.mutateAsync(revokeTarget.id)
      toast({ title: 'Delegation revoked' })
      setRevokeTarget(null)
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Revoke failed', variant: 'destructive' })
    }
  }

  const givenColumns = useMemo(() => [
    { accessorKey: 'delegate_email', header: 'Delegate',
      cell: ({ row }: { row: { original: ApprovalDelegation } }) => row.original.delegate_email,
    },
    { accessorKey: 'resource_types', header: 'Scope',
      cell: ({ row }: { row: { original: ApprovalDelegation } }) => (
        row.original.resource_types.length === 0
          ? <span className="text-muted-foreground">All approvals</span>
          : <span className="text-xs">{row.original.resource_types.join(', ')}</span>
      ),
    },
    { accessorKey: 'window', header: 'Window',
      cell: ({ row }: { row: { original: ApprovalDelegation } }) => (
        <span className="text-xs">{formatDateTime(row.original.active_from)} → {formatDateTime(row.original.active_to)}</span>
      ),
    },
    { accessorKey: 'status', header: 'Status',
      cell: ({ row }: { row: { original: ApprovalDelegation } }) => (
        row.original.revoked_at ? <Badge variant="secondary">revoked</Badge>
          : isActive(row.original) ? <Badge variant="success">active</Badge>
          : <Badge variant="default">scheduled</Badge>
      ),
    },
    { accessorKey: 'actions', header: '',
      cell: ({ row }: { row: { original: ApprovalDelegation } }) => (
        row.original.revoked_at ? null : (
          <Button size="icon" variant="ghost" className="size-7" onClick={() => setRevokeTarget(row.original)} aria-label="Revoke">
            <Trash2 className="size-4 text-destructive" />
          </Button>
        )
      ),
    },
  ], [])

  const receivedColumns = useMemo(() => [
    { accessorKey: 'delegator_email', header: 'Delegator',
      cell: ({ row }: { row: { original: ApprovalDelegation } }) => row.original.delegator_email,
    },
    { accessorKey: 'resource_types', header: 'Scope',
      cell: ({ row }: { row: { original: ApprovalDelegation } }) => (
        row.original.resource_types.length === 0
          ? <span className="text-muted-foreground">All approvals</span>
          : <span className="text-xs">{row.original.resource_types.join(', ')}</span>
      ),
    },
    { accessorKey: 'window', header: 'Window',
      cell: ({ row }: { row: { original: ApprovalDelegation } }) => (
        <span className="text-xs">{formatDateTime(row.original.active_from)} → {formatDateTime(row.original.active_to)}</span>
      ),
    },
    { accessorKey: 'status', header: 'Status',
      cell: ({ row }: { row: { original: ApprovalDelegation } }) => (
        row.original.revoked_at ? <Badge variant="secondary">revoked</Badge>
          : isActive(row.original) ? <Badge variant="success">active</Badge>
          : <Badge variant="default">scheduled</Badge>
      ),
    },
  ], [])

  return (
    <Page
      title="Approval delegations"
      description="Temporarily route your approvals to another member. Delegations apply to the approvals whose resource type you pick (or every approval if none are selected)."
      actions={
        <Button onClick={() => setOpen(true)}>
          <Plus className="mr-1 size-4" /> New delegation
        </Button>
      }
    >
      {isLoading ? (
        <Card><div className="flex justify-center py-16"><LoadingSpinner /></div></Card>
      ) : (
        <div className="space-y-4">
          <Card>
            <div className="p-4">
              <div className="mb-3 text-sm font-semibold">Delegations I&apos;ve given</div>
              {given.length === 0 ? (
                <EmptyState
                  icon={UserCog}
                  title="None yet"
                  description="Delegate your approvals when you&apos;re out of office."
                />
              ) : (
                <DataTable columns={givenColumns as never} rows={given} />
              )}
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="mb-3 text-sm font-semibold">Delegations given to me</div>
              {received.length === 0 ? (
                <EmptyState
                  icon={UserCog}
                  title="None yet"
                  description="Once someone delegates to you, their approvals will show up in your inbox with an &ldquo;On behalf of&rdquo; badge."
                />
              ) : (
                <DataTable columns={receivedColumns as never} rows={received} />
              )}
            </div>
          </Card>
        </div>
      )}

      <Modal open={open} onOpenChange={setOpen} title="New delegation">
        <div className="space-y-3">
          <FormField
            label="Delegate to"
            control={
              <select
                className="h-10 w-full rounded-md border bg-card px-3 text-sm"
                value={delegateId}
                onChange={e => setDelegateId(e.currentTarget.value)}
              >
                <option value="">Select a member…</option>
                {(members.data?.members ?? []).map(m => (
                  <option key={m.id} value={m.id}>
                    {m.email}{m.role_name ? ` - ${m.role_name}` : ''}
                  </option>
                ))}
              </select>
            }
          />
          <div className="grid gap-3 md:grid-cols-2">
            <FormField label="Active from" type="date" value={activeFrom} onChange={e => setActiveFrom(e.currentTarget.value)} />
            <FormField label="Active to" type="date" value={activeTo} onChange={e => setActiveTo(e.currentTarget.value)} />
          </div>
          <FormField
            label="Resource types"
            control={
              <div className="space-y-1 rounded-md border p-3 text-sm">
                {resourceTypes.length === 0 ? (
                  <span className="text-muted-foreground">Loading…</span>
                ) : (
                  <>
                    {resourceTypes.map(rt => (
                      <label key={rt} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedTypes.includes(rt)}
                          onChange={(e) => {
                            setSelectedTypes(prev => e.currentTarget.checked
                              ? Array.from(new Set([...prev, rt]))
                              : prev.filter(t => t !== rt))
                          }}
                        />
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{rt}</code>
                      </label>
                    ))}
                    <div className="mt-2 text-xs text-muted-foreground">Leave all unchecked to cover every approval type.</div>
                  </>
                )}
              </div>
            }
          />
          <FormField label="Reason" value={reason} onChange={e => setReason(e.currentTarget.value)} placeholder="e.g. Out of office May 1–8" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={onCreate} disabled={createMut.isPending}>Create</Button>
          </div>
        </div>
      </Modal>

      <Modal.Confirm
        open={revokeTarget !== null}
        onOpenChange={(o) => { if (!o) setRevokeTarget(null) }}
        title={revokeTarget ? `Revoke delegation to ${revokeTarget.delegate_email}?` : ''}
        body="The delegate will stop seeing your approvals immediately. Prior actions they took on your behalf stay recorded in the audit trail."
        confirmLabel="Revoke"
        confirmVariant="danger"
        busy={revokeMut.isPending}
        onConfirm={onRevokeConfirm}
      />
    </Page>
  )
}
